import React, { useState, useEffect } from 'react';
import { Add_platform_fee, List_platform_fee, Update_platform_fee, Delete_platform_fee } from '../../Utils/Api';
import { toast } from 'react-toastify';

const PlatformFees = () => {
    const [fees, setFees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        title: '',
        fee_type: 'fixed',
        value: '',
        status: 'active'
    });
    const [editingId, setEditingId] = useState(null);
    const [showModal, setShowModal] = useState(false);

    // Fetch data on component mount
    useEffect(() => {
        fetchPlatformFees();
    }, []);

    // Fetch all platform fees
    const fetchPlatformFees = async () => {
        setLoading(true);
        try {
            const response = await List_platform_fee();
            if (response && response.data.data) {
                setFees(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching platform fees:', error);
            toast.error('Failed to fetch platform fees');
        } finally {
            setLoading(false);
        }
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseFloat(value) : value
        }));
    };

    // Handle form submission (Add/Update)
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.title.trim()) {
            toast.error('Please enter title');
            return;
        }
        if (formData.value === '' || formData.value < 0) {
            toast.error('Please enter a valid non-negative value');
            return;
        }

        setLoading(true);
        try {
            const payload = { ...formData };
            // No cent conversion as per user: "all are in dollar"

            if (editingId) {
                // Update existing platform fee
                await Update_platform_fee(editingId, payload);
                toast.success('Platform fee updated successfully');
            } else {
                // Add new platform fee
                await Add_platform_fee(payload);
                toast.success('Platform fee added successfully');
            }

            // Reset form and refresh list
            resetForm();
            setShowModal(false);
            fetchPlatformFees();
        } catch (error) {
            console.error('Error saving platform fee:', error);
            toast.error('Failed to save platform fee');
        } finally {
            setLoading(false);
        }
    };

    // Reset form
    const resetForm = () => {
        setFormData({
            title: '',
            fee_type: 'fixed',
            value: '',
            status: 'active'
        });
        setEditingId(null);
    };

    // Handle edit
    const handleEdit = (fee) => {
        setFormData({
            title: fee.title || '',
            fee_type: fee.fee_type || 'fixed',
            value: fee.value || '',
            status: fee.status || 'active'
        });
        setEditingId(fee._id || fee.id);
        setShowModal(true);
    };

    // Open add modal
    const handleAdd = () => {
        resetForm();
        setShowModal(true);
    };

    // Format fee value for display
    const formatFeeValue = (fee) => {
        if (fee.fee_type === 'percentage') {
            return `${fee.value}%`;
        }
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(fee.value);
    };

    // Get badge color for status
    const getStatusBadge = (status) => {
        return status === 'active' ? 'bg-success' : 'bg-secondary';
    };

    return (
        <div className="main-content ">
            <div className="row my-4 mx-1">
                <div className="d-md-flex d-block align-items-center justify-content-between my-4 page-header-breadcrumb">
                    <h1 className="page-title fw-semibold fs-18 mb-0 mt-2">PlatformFees</h1>
                    <div className="ms-md-1 ms-0 ">
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item"><a href="#">Management</a></li>
                                <li className="breadcrumb-item active" aria-current="page">PlatformFees</li>
                            </ol>
                        </nav>
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="card custom-card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0">Platform Fees List</h6>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={handleAdd}
                        >
                            <i className="ri-add-line me-1"></i> Add Platform Fee
                        </button>
                    </div>
                    <div className="card-body">
                        <div className="table-responsive">
                            <table className="table text-nowrap table-hover">
                                <thead className="table-primary">
                                    <tr>
                                        <th scope="col">#</th>
                                        <th scope="col">Title</th>
                                        <th scope="col">Fee Type</th>
                                        <th scope="col">Value</th>
                                        <th scope="col">Status</th>
                                        <th scope="col">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && fees.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4">
                                                <div className="spinner-border text-primary" role="status">
                                                    <span className="visually-hidden">Loading...</span>
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {!loading && fees.length === 0 && (
                                        <tr>
                                            <td colSpan="6" className="text-center py-4">
                                                No platform fees found
                                            </td>
                                        </tr>
                                    )}

                                    {fees.map((fee, index) => (
                                        <tr key={fee._id || fee.id || index}>
                                            <th scope="row">{index + 1}</th>
                                            <td className="fw-semibold text-primary">{fee.title}</td>
                                            <td>
                                                <span className={`badge ${fee.fee_type === 'percentage' ? 'bg-warning-transparent' : 'bg-info-transparent'}`}>
                                                    {fee.fee_type.toUpperCase()}
                                                </span>
                                            </td>
                                            <td>
                                                <strong className="text-dark">{formatFeeValue(fee)}</strong>
                                            </td>
                                            <td>
                                                <span className={`badge ${getStatusBadge(fee.status)}`}>
                                                    {fee.status.charAt(0).toUpperCase() + fee.status.slice(1)}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="hstack gap-2 fs-15">
                                                    <button
                                                        onClick={() => handleEdit(fee)}
                                                        className="btn btn-icon btn-sm btn-info-transparent rounded-pill"
                                                        title="Edit"
                                                    >
                                                        <i className="ri-edit-line"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Add/Edit Modal */}
                {showModal && (
                    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        {editingId ? 'Edit Platform Fee' : 'Add New Platform Fee'}
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowModal(false)}
                                    ></button>
                                </div>
                                <form onSubmit={handleSubmit}>
                                    <div className="modal-body">
                                        <div className="row">
                                            {/* Title */}
                                            <div className="col-md-12 mb-3">
                                                <label htmlFor="title" className="form-label">Title <span className="text-danger">*</span></label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    id="title"
                                                    name="title"
                                                    value={formData.title}
                                                    onChange={handleInputChange}
                                                    placeholder="e.g., Checkr Fee, Stripe Fee"
                                                    required
                                                />
                                            </div>

                                            {/* Fee Type */}
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="fee_type" className="form-label">Fee Type <span className="text-danger">*</span></label>
                                                <select
                                                    className="form-select"
                                                    id="fee_type"
                                                    name="fee_type"
                                                    value={formData.fee_type}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="percentage">Percentage (%)</option>
                                                    <option value="fixed">Fixed Amount ($)</option>
                                                </select>
                                            </div>

                                            {/* Value */}
                                            <div className="col-md-6 mb-3">
                                                <label htmlFor="value" className="form-label">
                                                    Value <span className="text-danger">*</span>
                                                    {formData.fee_type === 'percentage' && ' (%)'}
                                                    {formData.fee_type === 'fixed' && ' ($)'}
                                                </label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    id="value"
                                                    name="value"
                                                    value={formData.value}
                                                    onChange={handleInputChange}
                                                    placeholder={formData.fee_type === 'percentage' ? "e.g., 5" : "e.g., 2.50"}
                                                    step="0.01"
                                                    min="0"
                                                    required
                                                />
                                            </div>

                                            {/* Status */}
                                            <div className="col-md-12 mb-3">
                                                <label htmlFor="status" className="form-label">Status</label>
                                                <select
                                                    className="form-select"
                                                    id="status"
                                                    name="status"
                                                    value={formData.status}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="active">Active</option>
                                                    <option value="inactive">Inactive</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => setShowModal(false)}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                    {editingId ? 'Updating...' : 'Adding...'}
                                                </>
                                            ) : (
                                                editingId ? 'Update' : 'Add'
                                            )}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PlatformFees;